import {
    useGetBenchVariations,
    useGetExtraVariations,
    useGetProductVariations,
} from 'components/customer/Confirmation/Checkout/helpers/useGetVariations';
import {totalRemovedVariationCostReset} from 'components/customer/Confirmation/store/confirmationSlice';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {
    invalidateVariations,
    useDeleteAllVariationsMutation,
} from 'components/customer/Job/store/jobApi';
import {
    useDialogContext,
    useJobContext,
    useNotificationContext,
} from 'contexts';
import {useCallback, useMemo} from 'react';
import {genericMessageHandler} from 'shared/helpers';
import {PartialJob} from 'shared/types/PartialJob';
import {useAppDispatch} from 'store/customer';

export const useDeleteAllVariations = (beforeAction?: () => void) => {
    const dispatch = useAppDispatch();
    const {job} = useJobContext() as PartialJob;
    const {showDialog} = useDialogContext();
    const {notify} = useNotificationContext();
    const [deletAllVariations] = useDeleteAllVariationsMutation();

    const {variations: benchVariations} = useGetBenchVariations();
    const {variations: productVariations} = useGetProductVariations();
    const {variations: extraVariations} = useGetExtraVariations();

    const showDeleteAllButton = useMemo(() => {
        return (
            (benchVariations.length > 0 ||
                productVariations.length > 0 ||
                extraVariations.filter((variation) => variation.removable)
                    .length > 0) &&
            job.status != JOB_STATUS.PROCESSING
        );
    }, [benchVariations, productVariations, extraVariations, job]);

    const deleteAllVariaitons = useCallback(() => {
        showDialog({
            title: 'Delete All Variations',
            message: `Are you sure you want to remove all variations associated with job ${job.displayId}?`,
            yes: async () => {
                typeof beforeAction == 'function' && beforeAction();
                try {
                    await deletAllVariations({jobId: job?.id});

                    dispatch(invalidateVariations());
                    dispatch(totalRemovedVariationCostReset());

                    genericMessageHandler(
                        notify,
                        {
                            message:
                                'Variations have been successfully removed.',
                        },
                        'success'
                    );
                } catch (error) {
                    genericMessageHandler(notify, {
                        message: 'Something went wrong',
                    });
                    throw error;
                }
            },
        });
    }, [job, showDialog]);

    return {
        deleteAllVariaitons,
        showDeleteAllButton,
    };
};
